import { forwardRef, useState, useEffect, useMemo } from 'react';
import { DataGrid, type DataGridProps } from '@mui/x-data-grid';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

// ==============================|| CUSTOM DATA GRID WITH DYNAMIC X SCROLL ||============================== //

interface DataGridWithDynamicXScrollProps extends Omit<DataGridProps, 'sx'> {
  sx?: SxProps<Theme>;
}

const DataGridWithDynamicXScroll = forwardRef<HTMLDivElement, DataGridWithDynamicXScrollProps>(({ sx = {}, density, ...others }, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { rows, loading } = others;
  const hasRows = Boolean(rows?.length);

  const effectiveDensity = isMobile ? 'compact' : density;

  const useIsInViewport = (el: Element | null) => {
    const [isIntersecting, setIsIntersecting] = useState(false);

    const observer = useMemo(
      () =>
        new IntersectionObserver(([entry]) => {
          setIsIntersecting(entry.isIntersecting);
        }),
      []
    );

    useEffect(() => {
      if (el) {
        observer.observe(el);
      }

      return () => {
        observer.disconnect();
      };
    }, [el, observer]);

    return isIntersecting;
  };

  const [footerElement, setFooterElement] = useState<Element | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const footer = document.getElementsByClassName('MuiDataGrid-footerContainer')[0];
      setFooterElement(footer || null);
    }, 100); // Delay of 100ms to allow rendering to stabilize

    return () => {
      clearTimeout(timeout);
    };
  }, [rows]); // Ensure footerElement is set after rows are rendered

  const isDataGridFooterInViewport = useIsInViewport(footerElement);

  return (
    <DataGrid
      ref={ref}
      density={effectiveDensity}
      getRowHeight={() => (isMobile ? 'auto' : null)}
      autosizeOptions={{
        includeOutliers: false,
        includeHeaders: !isMobile,
        expand: true
      }}
      sx={{
        '& .MuiDataGrid-main': {
          transform: `rotateX(${isDataGridFooterInViewport || !hasRows ? 0 : 180}deg)`
        },
        '& .MuiDataGrid-virtualScroller': {
          transform: `rotateX(${isDataGridFooterInViewport || !hasRows ? 0 : 180}deg)`
        },
        '& .MuiDataGrid-scrollbar': {
          position: isDataGridFooterInViewport || !hasRows ? 'absolute !important' : 'unset !important'
        },
        [theme.breakpoints.down('sm')]: {
          fontSize: '0.75rem',
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '0.75rem',
            fontWeight: 600
          },
          '& .MuiDataGrid-cell': {
            fontSize: '0.75rem'
          },
          '& .MuiDataGrid-toolbarContainer': {
            flexWrap: 'wrap',
            gap: 0.5,
            padding: '4px 8px'
          },
          '& .MuiDataGrid-footerContainer': {
            minHeight: 40,
            '& .MuiTablePagination-toolbar': {
              minHeight: 40,
              flexWrap: 'wrap',
              padding: '0 8px'
            },
            '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
              fontSize: '0.7rem'
            }
          }
        },
        ...sx
      }}
      {...others}
      hideFooter={Boolean(loading) || others.hideFooter}
    />
  );
});

DataGridWithDynamicXScroll.displayName = 'DataGridWithDynamicXScroll';

export default DataGridWithDynamicXScroll;
